import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import {
    catchError,
    map,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    take,
    tap,
} from 'rxjs';

/**
 * Admin inbox client — shared by the header bell and the Notifications page.
 *
 * Backed by the rows the API writes on every admin push
 * (webApi/src/utils/webPush.ts → a_notifications), so the same events a
 * browser toast announced stay listed until the admin removes them.
 *
 * `notifications$` is the bell's cache: the newest 50 UNREAD rows. The
 * dropdown is a tray of what still needs looking at — marking a row read
 * takes it out of the tray (it remains on the Notifications page, which is
 * the full history). The page queries the same endpoint with paging/filters
 * via `query()` and does not touch the cache — it calls `getAll()` after a
 * write so the badge catches up.
 */

/** One row as the API returns it. */
export interface AdminNotificationRow {
    id: number;
    title: string;
    body: string;
    url: string;
    eventType: string;
    createdAt: string;
    isRead: boolean;
}

export interface AdminNotificationQuery {
    page: number; // 1-based
    pageSize: number;
    eventType?: string;
    unreadOnly?: boolean;
}

export interface AdminNotificationPage {
    items: Notification[];
    total: number;
    unreadCount: number;
}

interface ListResponse {
    recordList: AdminNotificationRow[];
    totalRecords: number;
    unreadCount: number;
}

/** Bell icon per event category (eventType is derived server-side). */
export const EVENT_ICONS: Record<string, string> = {
    business: 'heroicons_mini:building-storefront',
    lead: 'heroicons_mini:user-plus',
    order: 'heroicons_mini:banknotes',
    ticket: 'heroicons_mini:lifebuoy',
    demo: 'heroicons_mini:calendar-days',
    partner: 'heroicons_mini:user-group',
    inquiry: 'heroicons_mini:envelope',
};

/** Filter options for the page; keys match the API's EVENT_TYPES. */
export const EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'business', label: 'Businesses' },
    { value: 'lead', label: 'Leads' },
    { value: 'inquiry', label: 'Inquiries' },
    { value: 'demo', label: 'Demo bookings' },
    { value: 'partner', label: 'Partners' },
    { value: 'order', label: 'Orders' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'other', label: 'Other' },
];

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private readonly _base = `${environment.apiUrl}/api/v0.4.0/saasSystemUser/admin-notifications`;

    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — bell (cached list)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the bell's notifications — unread only.
     *
     * Runs in the initial-data resolver, so a failure here must not block
     * the shell from rendering — swallow it and show an empty bell.
     */
    getAll(): Observable<Notification[]> {
        const params = new HttpParams().set('unreadOnly', '1');
        return this._httpClient
            .get<ListResponse>(`${this._base}/list`, { params })
            .pipe(
                map((res) => (res?.recordList ?? []).map(toNotification)),
                catchError(() => of([] as Notification[])),
                tap((notifications) => {
                    this._notifications.next(notifications);
                })
            );
    }

    /**
     * Update the notification — only `read` is user-editable from the bell.
     * The tray holds unread rows only, so a row marked read leaves it.
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this.markRead(Number(id), notification.read).pipe(
                    map(() => {
                        this._notifications.next(
                            notification.read
                                ? notifications.filter((item) => item.id !== id)
                                : [...notifications]
                        );
                        return notification;
                    })
                )
            )
        );
    }

    /**
     * Delete the notification (hides it for this admin only)
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this.remove(Number(id)).pipe(
                    map(() => {
                        this._notifications.next(
                            notifications.filter((item) => item.id !== id)
                        );
                        return true;
                    })
                )
            )
        );
    }

    /**
     * Mark all notifications as read — empties the tray.
     */
    markAllAsRead(): Observable<boolean> {
        return this.markAll().pipe(
            map(() => {
                this._notifications.next([]);
                return true;
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — page (uncached, paged)
    // -----------------------------------------------------------------------------------------------------

    /** One page of the inbox. Does not touch the bell's cache. */
    query(q: AdminNotificationQuery): Observable<AdminNotificationPage> {
        let params = new HttpParams()
            .set('page', String(q.page))
            .set('pageSize', String(q.pageSize));
        if (q.eventType) {
            params = params.set('eventType', q.eventType);
        }
        if (q.unreadOnly) {
            params = params.set('unreadOnly', '1');
        }
        return this._httpClient
            .get<ListResponse>(`${this._base}/list`, { params })
            .pipe(
                map((res) => ({
                    items: (res?.recordList ?? []).map(toNotification),
                    total: res?.totalRecords ?? 0,
                    unreadCount: res?.unreadCount ?? 0,
                }))
            );
    }

    /** Raw write — the bell wraps these to keep its cache in step. */
    markRead(id: number, isRead: boolean): Observable<unknown> {
        return this._httpClient.post(`${this._base}/mark-read`, { id, isRead });
    }

    remove(id: number): Observable<unknown> {
        return this._httpClient.post(`${this._base}/remove`, { id });
    }

    markAll(): Observable<unknown> {
        return this._httpClient.post(`${this._base}/mark-all-read`, {});
    }
}

/**
 * The template binds title/description with [innerHTML]. Bodies carry text
 * typed by the public (lead names, ticket subjects), so escape it — Angular's
 * sanitizer would strip scripts anyway, but a literal "<b>" in a name should
 * show as typed. Newlines (partner follow-up digests) become line breaks.
 */
function toHtml(text: string): string {
    return (text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
}

function toNotification(row: AdminNotificationRow): Notification {
    return {
        id: String(row.id),
        icon: EVENT_ICONS[row.eventType] ?? 'heroicons_mini:bell',
        title: toHtml(row.title),
        description: toHtml(row.body),
        time: row.createdAt,
        link: row.url,
        useRouter: true,
        read: row.isRead,
    };
}
